import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Input,
  Modal,
  Section,
  Text,
  useToast,
} from 'barte-design-system';
import {
  Bolsao,
  formatBRL,
  formatPercent,
} from './bolsao-mock-data';
import './Bolsao.css';

interface Props {
  bolsao: Bolsao | null;
  isOpen: boolean;
  /** Abre o drawer já em modo de edição (ex.: via ícone de editar na linha) */
  startInEdit?: boolean;
  onClose: () => void;
  onUpdateConfig: (id: string, percentTpv: number, teto: number | null) => void;
}

/**
 * Drawer de detalhe do bolsão — identificação e configuração editável
 * (edição em modo focado, com confirmação em Modal).
 */
export function DrawerDetalheBolsao({ bolsao, isOpen, startInEdit, onClose, onUpdateConfig }: Props) {
  const toast = useToast();

  const [editing, setEditing] = useState(false);
  const [percent, setPercent] = useState('');
  const [teto, setTeto] = useState('');
  const [confirmEdicao, setConfirmEdicao] = useState(false);

  // Sincroniza os campos de edição quando o bolsão selecionado muda
  useEffect(() => {
    if (bolsao) {
      setPercent(String(bolsao.percentTpv).replace('.', ','));
      setTeto(bolsao.teto !== null ? formatBRL(bolsao.teto) : '');
    }
    setEditing(!!startInEdit && !!bolsao);
    setConfirmEdicao(false);
  }, [bolsao, startInEdit]);

  if (!bolsao) return null;

  function parseNumber(value: string): number | null {
    const n = Number(value.replace(/[R$\s.]/g, '').replace(',', '.'));
    return Number.isFinite(n) && value.trim() !== '' ? n : null;
  }

  function handleCancelEdit() {
    if (!bolsao) return;
    setPercent(String(bolsao.percentTpv).replace('.', ','));
    setTeto(bolsao.teto !== null ? formatBRL(bolsao.teto) : '');
    setEditing(false);
  }

  function handleConfirmEdit() {
    if (!bolsao) return;
    const percentValue = parseNumber(percent);
    if (percentValue === null) return;
    onUpdateConfig(bolsao.id, percentValue, parseNumber(teto));
    setConfirmEdicao(false);
    setEditing(false);
    toast.success({
      title: 'Configuração atualizada',
      description: 'O bolsão esperado foi recalculado com a nova configuração.',
    });
  }

  return (
    <>
      <Drawer
        isOpen={isOpen}
        title={bolsao.payfac}
        onClose={onClose}
        className="bolsao-drawer-detalhe"
        footer={
          editing
            ? {
                primaryButton: {
                  children: 'Salvar',
                  onClick: () => setConfirmEdicao(true),
                  disabled: percent.trim() === '',
                },
                secondaryButton: {
                  children: 'Cancelar',
                  variant: 'outline',
                  onClick: handleCancelEdit,
                },
              }
            : {
                primaryButton: {
                  children: 'Editar configuração',
                  onClick: () => setEditing(true),
                },
              }
        }
      >
        <div className="bolsao-detalhe">
          {/* ── Identificação ── */}
          <div className="bolsao-detalhe-head">
            <div className="bolsao-detalhe-head-info">
              <Text variant="body-xs" className="bolsao-detalhe-meta">
                ID: {bolsao.payfacId}
              </Text>
              <Text variant="body-xs" className="bolsao-detalhe-meta">
                Última alteração: {bolsao.ultimaAlteracao.usuario} · {bolsao.ultimaAlteracao.data}
              </Text>
            </div>
          </div>

          {/* ── Configuração ── */}
          <Section title="Configuração">
            {editing ? (
              <div className="bolsao-form-row bolsao-edit-fields">
                <Input
                  label="% sobre TPV"
                  value={percent}
                  onChange={(e) => setPercent((e.target as HTMLInputElement).value)}
                />
                <Input
                  label="Teto"
                  placeholder="Sem teto"
                  helpText="Deixe em branco para não definir teto."
                  value={teto}
                  onChange={(e) => setTeto((e.target as HTMLInputElement).value)}
                />
              </div>
            ) : (
              <>
                <Section.Row label="% sobre TPV" content={formatPercent(bolsao.percentTpv)} />
                <Section.Row
                  label="Teto"
                  content={bolsao.teto !== null ? formatBRL(bolsao.teto) : 'Sem teto'}
                />
              </>
            )}
          </Section>
        </div>
      </Drawer>

      {/* ── Modal: confirmação de edição da configuração ── */}
      <Modal
        isOpen={confirmEdicao}
        title="Confirmar alteração da configuração"
        onClose={() => setConfirmEdicao(false)}
        size="sm"
        className="bolsao-modal-sobre-drawer"
        footer={{
          primaryButton: { children: 'Confirmar', onClick: handleConfirmEdit },
          secondaryButton: {
            children: 'Cancelar',
            variant: 'outline',
            onClick: () => setConfirmEdicao(false),
          },
        }}
      >
        <Text variant="body-sm">
          Ao confirmar, o bolsão esperado de <strong>{bolsao.payfac}</strong> será recalculado
          com a nova configuração aplicada sobre o TPV acumulado desde a criação — o período
          não é reiniciado.
        </Text>
      </Modal>
    </>
  );
}
